/**
 * PremiumGate — contextual teaser shown when a Lite user reaches a gated feature.
 *
 * Describes what the feature is and offers one calm path to the Premium page.
 * The actual checkout lives in PremiumPage (/premium) — not here. This keeps
 * every gated surface consistent and avoids per-screen checkout buttons.
 *
 * Not used on never-gated surfaces (ED screener, GDPR export, logging).
 */

import type { PremiumFeature } from '@nutians/shared/schemas';
import { useTranslation } from 'react-i18next';
import { usePremiumDialog } from '../context/PremiumDialogContext.js';

interface PremiumGateProps {
  feature: PremiumFeature;
  heading?: string;
  body?: string;
}

export function PremiumGate({ feature, heading, body }: PremiumGateProps) {
  const { t } = useTranslation('paywall');
  const { openDialog } = usePremiumDialog();
  const title = heading ?? t(`features.${feature}.heading`);
  const desc = body ?? t(`features.${feature}.body`);

  return (
    <div className="rounded-xl border border-husk bg-paper px-4 py-4 dark:border-ink/20 dark:bg-shell">
      <p className="text-sm font-semibold text-ink">
        {title} <span className="font-normal text-ink-soft">· Premium</span>
      </p>
      <p className="mt-1 text-sm leading-5 text-ink-soft">{desc}</p>
      <button
        type="button"
        onClick={openDialog}
        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-leaf
                   hover:text-leaf-deep focus:outline-none focus:ring-2 focus:ring-leaf
                   focus:ring-offset-1 rounded"
      >
        See Premium plans
        <span aria-hidden>→</span>
      </button>
    </div>
  );
}
